import { create } from "zustand";
import type { User } from "@/types/auth";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  hydrate: () => void;
  setAuth: (user?: User) => void;
  setUser: (user: User) => void;
  logout: () => void;
}

const SESSION_KEY = "auth";

function setSessionCookie() {
  if (typeof document === "undefined") return;
  document.cookie = "session=1;path=/;max-age=86400;SameSite=Lax";
}

function clearSessionCookie() {
  if (typeof document === "undefined") return;
  document.cookie = "session=;path=/;max-age=0;SameSite=Lax";
}

function hasSessionCookie() {
  if (typeof document === "undefined") return false;
  return /(?:^|;\s*)session=1(?:;|$)/.test(document.cookie);
}

function saveSession(user: User | null) {
  if (typeof window === "undefined") return;
  if (user) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user }));
  } else {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

function loadSession(): Partial<AuthState> {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (data.user) {
      setSessionCookie();
      return { user: data.user, isAuthenticated: true };
    }
  } catch {
    // Ignore corrupted session storage and fall back to the session cookie.
  }

  // Mobile browsers can discard the tab process and later restore it with
  // cookies intact but sessionStorage cleared. Preserve the authenticated
  // bootstrap state so current-user can rehydrate from the API.
  if (hasSessionCookie()) {
    return { isAuthenticated: true };
  }

  return {};
}

let hydrated = false;

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,

  hydrate: () => {
    if (hydrated) return;
    hydrated = true;
    const restored = loadSession();
    if (restored.user !== undefined || restored.isAuthenticated !== undefined) {
      set({
        user: restored.user ?? null,
        isAuthenticated: restored.isAuthenticated ?? false,
      });
    }
  },

  setAuth: (user) => {
    set({
      isAuthenticated: true,
      ...(user ? { user } : {}),
    });
    setSessionCookie();
    saveSession(user ?? useAuthStore.getState().user);
  },

  setUser: (user) => {
    set({ user });
    saveSession(user);
  },

  logout: () => {
    clearSessionCookie();
    set({ user: null, isAuthenticated: false });
    saveSession(null);
  },
}));
